import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { errorMessage } from '../../lib/api';
import { recordWhatsappClick } from '../../lib/distribution';
import { useDragAutoScroll } from '../../lib/kanban-autoscroll';
import {
  readKanbanPosition,
  saveBoardScroll,
  saveColumnScroll,
  saveOpenedBusiness,
  type ColumnPosition,
} from '../../lib/kanban-position';
import {
  Banknote,
  CalendarClock,
  CalendarDays,
  Check,
  MessageCircle,
  Plus,
  UserRound,
  X,
} from 'lucide-react';
import {
  daysSince,
  formatDate,
  formatDateTime,
  formatMoney,
  isPast,
  whatsappLink,
} from '../../lib/format';
import {
  listAttendants,
  listBusinesses,
  moveBusinesses,
  pipelinesApi,
  winBusinesses,
  type BusinessAction,
} from '../../lib/resources';
import type { Attendant, Business, PipelineComplete, Stage, StageSummary } from '../../types';
import { LoseBusinessModal, NewBusinessModal } from '../BusinessActionModals';
import { Menu } from '../Menu';
import { Avatar, ConfirmDialog, KindBadge, TagPill } from '../ui';
import { DeleteStageModal, StageFormModal } from './PipelineModals';

const PAGE = 20;
/** Colunas guardadas por 30 min: ao voltar de um card, as páginas já carregadas continuam lá. */
const COLUMN_CACHE_MS = 30 * 60 * 1000;
/** Ao restaurar a posição, recarrega no máximo estas páginas por coluna. */
const MAX_RESTORE_PAGES = 15;
const HIGHLIGHT_MS = 2500;

/** Vai junto do link do card: o X do negócio volta para o quadro com os mesmos filtros. */
export interface BoardLinkState {
  board: string;
}

export interface BoardToast {
  tone: 'error' | 'info';
  text: string;
}

/**
 * Kanban da pipeline. Cada coluna pagina sozinha ("carregar mais"); o
 * cabeçalho soma pelo resumo da API, com os mesmos filtros da tela.
 * Arrastar card move o negócio (as condicionais da etapa podem barrar — o
 * toast mostra a mensagem); arrastar o cabeçalho reordena as colunas.
 * Durante o arrastar, o quadro rola sozinho pela posição do cursor
 * (useDragAutoScroll). A rolagem do quadro e das colunas fica guardada
 * (kanban-position): ao voltar de um card, o quadro reabre no mesmo lugar, com
 * o card aberto destacado.
 */
export function KanbanBoard({
  pipeline,
  stages,
  summary,
  filter,
  onChanged,
  onBulk,
  onToast,
}: {
  pipeline: PipelineComplete;
  stages: Stage[];
  summary: StageSummary[];
  filter: string;
  onChanged: () => void;
  onBulk: (action: BusinessAction, stage: Stage) => void;
  onToast: (toast: BoardToast) => void;
}) {
  const [dragging, setDragging] = useState<{ kind: 'business' | 'stage'; id: string } | null>(
    null,
  );
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [newColumn, setNewColumn] = useState(false);
  const [editing, setEditing] = useState<Stage | null>(null);
  const [deleting, setDeleting] = useState<Stage | null>(null);
  const [newBusinessStage, setNewBusinessStage] = useState<Stage | null>(null);
  const [winning, setWinning] = useState<Business | null>(null);
  const [losing, setLosing] = useState<Business | null>(null);
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });
  const boardRef = useRef<HTMLDivElement>(null);
  const [saved] = useState(() => readKanbanPosition(pipeline.id));
  const [highlightId, setHighlightId] = useState(saved.openedBusinessId);

  useDragAutoScroll(boardRef, dragging !== null);

  useLayoutEffect(() => {
    if (boardRef.current) boardRef.current.scrollLeft = saved.scrollLeft;
  }, [saved]);

  useEffect(() => {
    if (!saved.openedBusinessId) return;
    saveOpenedBusiness(pipeline.id);
    const timer = window.setTimeout(() => setHighlightId(undefined), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [pipeline.id, saved]);

  const move = useMutation({
    mutationFn: ({ business, stage }: { business: Business; stage: Stage }) =>
      moveBusinesses([business.id], stage.id),
    onSuccess: (_result, { stage }) => {
      onChanged();
      if (stage.autoActivitiesCount > 0) {
        onToast({
          tone: 'info',
          text: `${stage.autoActivitiesCount} atividade(s) criada(s) pela etapa ${stage.name}`,
        });
      }
    },
    onError: (error) =>
      onToast({ tone: 'error', text: errorMessage(error, 'Não foi possível mover o negócio') }),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => pipelinesApi.reorderStages(pipeline.id, ids),
    onSuccess: onChanged,
    onError: (error) =>
      onToast({ tone: 'error', text: errorMessage(error, 'Falha ao reordenar') }),
  });

  const win = useMutation({
    mutationFn: (business: Business) => winBusinesses([business.id]),
    onSuccess: () => {
      setWinning(null);
      onChanged();
    },
  });

  const totalsOf = (stageId: string) =>
    summary.find((item) => item.stageId === stageId) ?? { businessesCount: 0, total: 0 };

  const dropOn = (stage: Stage, payload: { business?: Business; stageId?: string }) => {
    setDropTarget(null);
    setDragging(null);
    if (payload.business && payload.business.stageId !== stage.id) {
      move.mutate({ business: payload.business, stage });
    }
    if (payload.stageId && payload.stageId !== stage.id) {
      const ids = stages.map((item) => item.id).filter((id) => id !== payload.stageId);
      ids.splice(ids.indexOf(stage.id), 0, payload.stageId);
      reorder.mutate(ids);
    }
  };

  return (
    <div
      ref={boardRef}
      onScroll={(event) => saveBoardScroll(pipeline.id, event.currentTarget.scrollLeft)}
      className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-3"
    >
      {stages.map((stage) => (
        <KanbanColumn
          key={stage.id}
          pipelineId={pipeline.id}
          stage={stage}
          savedPosition={saved.columns[stage.id]}
          focusId={saved.openedBusinessId}
          highlightId={highlightId}
          filter={filter}
          totals={totalsOf(stage.id)}
          attendants={attendants.data?.data ?? []}
          canManage={pipeline.canManage}
          canCreate={!pipeline.myFlags.disableCreate}
          dragging={dragging}
          isTarget={dropTarget === stage.id}
          onDragState={setDragging}
          onDragOver={() => setDropTarget(stage.id)}
          onDragLeave={() =>
            setDropTarget((current) => (current === stage.id ? null : current))
          }
          onDrop={(payload) => dropOn(stage, payload)}
          onEdit={() => setEditing(stage)}
          onDelete={() => setDeleting(stage)}
          onNewBusiness={() => setNewBusinessStage(stage)}
          onBulk={(action) => onBulk(action, stage)}
          onWin={setWinning}
          onLose={setLosing}
        />
      ))}

      {pipeline.canManage && (
        <button
          type="button"
          onClick={() => setNewColumn(true)}
          disabled={stages.length >= 25}
          title={stages.length >= 25 ? 'Máximo de 25 etapas' : undefined}
          className="h-12 w-60 shrink-0 rounded-xl border-2 border-dashed border-slate-300 text-sm font-medium text-slate-500 hover:border-slate-400 hover:text-slate-700 disabled:opacity-40"
        >
          + Nova coluna
        </button>
      )}

      {newColumn && (
        <StageFormModal
          pipelineId={pipeline.id}
          onClose={() => setNewColumn(false)}
          onSaved={onChanged}
        />
      )}
      {editing && (
        <StageFormModal
          pipelineId={pipeline.id}
          stage={editing}
          onClose={() => setEditing(null)}
          onSaved={onChanged}
        />
      )}
      {deleting && (
        <DeleteStageModal
          stage={deleting}
          stages={stages}
          summary={summary.find((item) => item.stageId === deleting.id)}
          onClose={() => setDeleting(null)}
          onDeleted={onChanged}
        />
      )}
      {newBusinessStage && (
        <NewBusinessModal
          stage={newBusinessStage}
          onClose={() => setNewBusinessStage(null)}
          onCreated={onChanged}
        />
      )}
      {winning && (
        <ConfirmDialog
          title="Marcar como ganho"
          message={
            <>
              Marcar o negócio <strong>#{winning.code}</strong> de{' '}
              {winning.lead?.name ?? 'lead'} ({formatMoney(winning.total)}) como ganho?
            </>
          }
          confirmLabel="Ganhar"
          busy={win.isPending}
          error={win.error ? errorMessage(win.error, 'Falha ao ganhar') : null}
          onConfirm={() => win.mutate(winning)}
          onClose={() => setWinning(null)}
        />
      )}
      {losing && (
        <LoseBusinessModal
          businessId={losing.id}
          onClose={() => setLosing(null)}
          onDone={onChanged}
        />
      )}
    </div>
  );
}

function KanbanColumn({
  pipelineId,
  stage,
  savedPosition,
  focusId,
  highlightId,
  filter,
  totals,
  attendants,
  canManage,
  canCreate,
  dragging,
  isTarget,
  onDragState,
  onDragOver,
  onDragLeave,
  onDrop,
  onEdit,
  onDelete,
  onNewBusiness,
  onBulk,
  onWin,
  onLose,
}: {
  pipelineId: string;
  stage: Stage;
  savedPosition?: ColumnPosition;
  focusId?: string;
  highlightId?: string;
  filter: string;
  totals: { businessesCount: number; total: number };
  attendants: Attendant[];
  canManage: boolean;
  canCreate: boolean;
  dragging: { kind: 'business' | 'stage'; id: string } | null;
  isTarget: boolean;
  onDragState: (state: { kind: 'business' | 'stage'; id: string } | null) => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: (payload: { business?: Business; stageId?: string }) => void;
  onEdit: () => void;
  onDelete: () => void;
  onNewBusiness: () => void;
  onBulk: (action: BusinessAction) => void;
  onWin: (business: Business) => void;
  onLose: (business: Business) => void;
}) {
  const query = useInfiniteQuery({
    queryKey: ['kanban', 'column', stage.id, filter],
    queryFn: ({ pageParam }) =>
      listBusinesses({ stageId: stage.id, skip: pageParam, take: PAGE }, filter),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < last.count ? loaded : undefined;
    },
    gcTime: COLUMN_CACHE_MS,
  });
  const pages = query.data?.pages.length ?? 0;
  const cards = (query.data?.pages ?? []).flatMap((page) => page.data);
  const scrollRef = useRef<HTMLDivElement>(null);
  const restored = useRef(!savedPosition);
  const wantedPages = Math.min(savedPosition?.pages ?? 0, MAX_RESTORE_PAGES);

  // Restaurar: primeiro recarrega as páginas que estavam abertas ("Carregar
  // mais"), depois devolve a rolagem e traz o card aberto para a vista.
  useEffect(() => {
    if (restored.current || query.isFetching || !query.hasNextPage) return;
    if (pages > 0 && pages < wantedPages) query.fetchNextPage();
  }, [pages, wantedPages, query]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (restored.current || !element || query.isLoading) return;
    if (pages < wantedPages && query.hasNextPage) return;
    restored.current = true;
    element.scrollTop = savedPosition?.scrollTop ?? 0;
    const card = focusId
      ? element.querySelector<HTMLElement>(`[data-business-id="${focusId}"]`)
      : null;
    card?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [pages, wantedPages, query.isLoading, query.hasNextPage, savedPosition, focusId]);

  const draggedCard =
    dragging?.kind === 'business' ? cards.find((card) => card.id === dragging.id) : undefined;
  const showAutoHint =
    isTarget && dragging?.kind === 'business' && !draggedCard && stage.autoActivitiesCount > 0;

  return (
    <section
      onDragOver={(event) => {
        event.preventDefault();
        onDragOver();
      }}
      onDragLeave={onDragLeave}
      onDrop={(event) => {
        event.preventDefault();
        const stageId = event.dataTransfer.getData('text/stage-id');
        const raw = event.dataTransfer.getData('text/business');
        onDrop(stageId ? { stageId } : raw ? { business: JSON.parse(raw) as Business } : {});
      }}
      className={`flex w-[19rem] shrink-0 flex-col overflow-hidden rounded-xl border border-t-4 bg-slate-100/70 transition ${
        isTarget ? 'border-slate-400 bg-slate-200/60' : 'border-slate-200'
      }`}
      style={{ borderTopColor: stage.color ?? '#94a3b8' }}
    >
      <header
        draggable={canManage}
        onDragStart={(event) => {
          event.dataTransfer.setData('text/stage-id', stage.id);
          event.dataTransfer.effectAllowed = 'move';
          onDragState({ kind: 'stage', id: stage.id });
        }}
        onDragEnd={() => onDragState(null)}
        className={`px-3 pb-2 pt-2.5 ${canManage ? 'cursor-grab' : ''}`}
      >
        <div className="flex items-center justify-between gap-2">
          <h2
            className="flex min-w-0 items-center gap-1.5 text-[13px] font-semibold uppercase text-slate-800"
            title={stage.name}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: stage.color ?? '#94a3b8' }}
            />
            <span className="truncate">{stage.name}</span>
          </h2>
          <div className="flex items-center">
            <Menu
              trigger={(toggle) => (
                <button
                  type="button"
                  onClick={toggle}
                  aria-label={`Ações da etapa ${stage.name}`}
                  className="rounded px-1.5 text-slate-400 hover:bg-slate-100"
                >
                  ⋯
                </button>
              )}
              groups={[
                {
                  items: [
                    { label: 'Editar etapa', onSelect: onEdit, disabled: !canManage },
                    { label: 'Mover negócios da etapa…', onSelect: () => onBulk('move') },
                    { label: 'Ganhar negócios da etapa…', onSelect: () => onBulk('win') },
                    { label: 'Perder negócios da etapa…', onSelect: () => onBulk('lose') },
                    {
                      label: 'Excluir negócios da etapa…',
                      onSelect: () => onBulk('delete'),
                      danger: true,
                    },
                  ],
                },
                {
                  items: [
                    {
                      label: 'Excluir etapa',
                      onSelect: onDelete,
                      disabled: !canManage,
                      danger: true,
                    },
                  ],
                },
              ]}
            />
          </div>
        </div>
        <p className="mt-0.5 flex items-center justify-between pl-4 text-xs text-slate-500">
          <span>{formatMoney(totals.total)}</span>
          <span>
            {totals.businessesCount === 0
              ? 'Nenhum negócio'
              : `${totals.businessesCount} ${totals.businessesCount === 1 ? 'Negócio' : 'Negócios'}`}
          </span>
        </p>
        {stage.autoActivitiesCount > 0 && (
          <p className="mt-0.5 text-[11px] text-sky-600">
            ⚡ cria {stage.autoActivitiesCount} atividade(s) ao entrar
          </p>
        )}
      </header>

      {showAutoHint && (
        <p className="mx-2 mt-2 rounded-lg bg-sky-50 px-2 py-1.5 text-[11px] text-sky-700">
          Ao soltar, {stage.autoActivitiesCount} atividade(s) serão criadas.
        </p>
      )}

      <div
        ref={scrollRef}
        data-kanban-scroll=""
        onScroll={(event) => {
          restored.current = true;
          saveColumnScroll(pipelineId, stage.id, {
            scrollTop: event.currentTarget.scrollTop,
            pages,
          });
        }}
        className="min-h-0 flex-1 space-y-2 overflow-y-auto px-2 pb-2"
      >
        {cards.map((business) => (
          <BusinessCard
            key={business.id}
            business={business}
            highlighted={highlightId === business.id}
            onOpen={() => saveOpenedBusiness(pipelineId, business.id)}
            attendant={attendants.find((item) => item.userId === business.attendantId)}
            isDragging={dragging?.id === business.id}
            onDragStart={(event) => {
              event.dataTransfer.setData(
                'text/business',
                JSON.stringify({ id: business.id, stageId: business.stageId }),
              );
              event.dataTransfer.effectAllowed = 'move';
              onDragState({ kind: 'business', id: business.id });
            }}
            onDragEnd={() => onDragState(null)}
            onWin={() => onWin(business)}
            onLose={() => onLose(business)}
          />
        ))}
        {query.isLoading && (
          <p className="px-2 py-6 text-center text-xs text-slate-400">Carregando…</p>
        )}
        {query.hasNextPage && (
          <button
            type="button"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="w-full rounded-lg py-2 text-xs font-medium text-slate-500 hover:bg-white"
          >
            {query.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
          </button>
        )}
      </div>
      {canCreate && (
        <button
          type="button"
          onClick={onNewBusiness}
          className="flex items-center justify-center gap-1 border-t border-slate-200 py-2 text-xs text-slate-500 hover:bg-slate-200/60 hover:text-slate-800"
        >
          <Plus size={14} /> Novo negócio
        </button>
      )}
    </section>
  );
}

function BusinessCard({
  business,
  attendant,
  highlighted,
  isDragging,
  onOpen,
  onDragStart,
  onDragEnd,
  onWin,
  onLose,
}: {
  business: Business;
  attendant?: Attendant;
  highlighted: boolean;
  isDragging: boolean;
  onOpen: () => void;
  onDragStart: (event: React.DragEvent) => void;
  onDragEnd: () => void;
  onWin: () => void;
  onLose: () => void;
}) {
  const location = useLocation();
  const lead = business.lead;
  const wa = whatsappLink(lead?.rawPhone);
  const firstProduct = business.products?.[0]?.product.name;
  const next = business.nextActivity;
  const overdue = isPast(next?.startDate);
  const ribbon =
    business.status === 'won'
      ? { text: 'Ganho', className: 'bg-emerald-500' }
      : business.status === 'lost'
        ? { text: 'Perdido', className: 'bg-red-500' }
        : null;

  const name = lead?.name ?? 'Lead';
  const row = 'flex items-center gap-2 text-xs text-slate-500';

  return (
    <article
      draggable
      data-business-id={business.id}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`group relative rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition ${
        isDragging ? 'opacity-40' : 'hover:border-slate-300'
      } ${highlighted ? 'ring-2 ring-[var(--crm-primary)] ring-offset-1' : ''}`}
    >
      {ribbon && (
        <span
          className={`absolute right-2 top-0 flex h-5 w-5 items-center justify-center rounded-b text-white ${ribbon.className}`}
          title={ribbon.text}
          aria-label={ribbon.text}
        >
          {business.status === 'won' ? (
            <Check size={12} strokeWidth={3} />
          ) : (
            <X size={12} strokeWidth={3} />
          )}
        </span>
      )}

      <div className="flex items-start gap-2.5">
        <Avatar name={name} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 pr-6">
            <Link
              to={`/negocios/${business.id}`}
              state={
                { board: `${location.pathname}${location.search}` } satisfies BoardLinkState
              }
              onClick={onOpen}
              className="truncate text-[13px] font-semibold text-slate-900 hover:underline"
              title={name}
            >
              {name}
            </Link>
            {lead?.kind && <KindBadge kind={lead.kind} />}
          </div>
          <p className="truncate text-[11px] text-sky-600">{firstProduct ?? 'Sem produto'}</p>
        </div>
        <span className={`shrink-0 text-[11px] text-slate-400 ${ribbon ? 'mr-6' : ''}`}>
          #{business.code}
        </span>
      </div>

      <div className="mt-2.5 space-y-1.5">
        <p className={row}>
          {attendant?.name ? (
            <>
              <Avatar name={attendant.name} size="xs" />
              <span className="truncate">{attendant.name}</span>
            </>
          ) : (
            <>
              <UserRound size={14} className="shrink-0" />
              <span>Sem atendente</span>
            </>
          )}
        </p>
        <p className={row}>
          <Banknote size={14} className="shrink-0" />
          <span className="font-medium text-slate-700">{formatMoney(business.total)}</span>
        </p>
        <p className={row}>
          <CalendarDays size={14} className="shrink-0" />
          <span>{formatDate(business.createdAt)}</span>
          <span className="text-slate-400">
            · {daysSince(business.lastMovedAt ?? business.createdAt)}d na etapa
          </span>
        </p>
        <div className={`${row} justify-between`}>
          <span
            className={`flex min-w-0 items-center gap-2 ${overdue ? 'font-medium text-red-600' : ''}`}
            title={next ? `${next.title} · ${formatDateTime(next.startDate)}` : undefined}
          >
            <CalendarClock size={14} className="shrink-0" />
            <span className="truncate">
              {next ? `${overdue ? 'Atrasada: ' : ''}${next.title}` : 'Sem atividades'}
            </span>
          </span>
          {wa && (
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              onClick={() => recordWhatsappClick(lead?.id ?? business.leadId)}
              className="shrink-0 text-emerald-600 hover:text-emerald-700"
              title="Abrir conversa no WhatsApp"
              aria-label="Abrir conversa no WhatsApp"
            >
              <MessageCircle size={15} />
            </a>
          )}
        </div>
      </div>

      {(lead?.tags ?? []).length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1 border-t border-slate-100 pt-2">
          {lead?.tags.map((tag) => (
            <TagPill key={tag.id} tag={tag} />
          ))}
        </div>
      )}

      {business.status === 'in_process' && (
        <div className="mt-2 hidden justify-end gap-1 group-hover:flex">
          <button
            type="button"
            onClick={onWin}
            className="rounded px-1.5 py-0.5 text-[11px] text-emerald-700 hover:bg-emerald-50"
          >
            Ganhar
          </button>
          <button
            type="button"
            onClick={onLose}
            className="rounded px-1.5 py-0.5 text-[11px] text-red-600 hover:bg-red-50"
          >
            Perder
          </button>
        </div>
      )}
    </article>
  );
}
